import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { Chip } from '../Chip/Chip';
import { styled } from '@mui/material/styles';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Avatar } from '../Avatar/Avatar';
import { NodeData, NodeProps as BaseNodeProps } from '../../../constants/interfaces';

const NodeContainer = styled(Paper)<{ isRoot?: boolean }>(({ theme, isRoot }) => ({
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 50, // Very rounded - true pill shape
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  cursor: isRoot ? 'default' : 'pointer',
  transition: 'all 0.2s ease-in-out',
  width: 180, // Fixed consistent width
  height: 32, // Fixed height for consistent pill shape
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  fontSize: '0.75rem',
  // Only apply hover effects for non-root nodes
  ...(!isRoot && {
    '&:hover': {
      boxShadow: theme.shadows[1],
      borderColor: theme.palette.primary.light,
    },
  }),
}));

const NodeContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const NodeName = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: 1.1,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// const NodeInfo = styled(Typography)(({ theme }) => ({
//   fontSize: '0.65rem',
//   color: theme.palette.text.secondary,
//   lineHeight: 1,
//   whiteSpace: 'nowrap',
//   overflow: 'hidden',
//   textOverflow: 'ellipsis',
// }));

const LevelBadge = styled(Box)(({ theme }) => ({
  width: '16px !important',
  height: '16px !important',
  borderRadius: '50% !important',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: '0.65rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minWidth: '16px !important',
  minHeight: '16px !important',
  maxWidth: '16px !important',
  maxHeight: '16px !important',
  boxSizing: 'border-box',
}));

const ExpandButton = styled(Button)(({ theme }) => ({
  minWidth: 16,
  width: 16,
  height: 16,
  maxWidth: 16,
  maxHeight: 16,
  minHeight: 16, // Override Material-UI's 44px min-height
  padding: 0,
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 10,
    width: 10,
    height: 10,
  },
}));

const MemberCountDisplay = styled(Box)(({ theme }) => ({
  fontSize: '0.65rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  marginTop: 1,
}));

// Re-export interfaces for backward compatibility
export type { NodeData };
export interface NodeProps extends BaseNodeProps {
  isSelected?: boolean;
  showExpandButton?: boolean;
}

// Smart comparison function for React.memo
const areNodePropsEqual = (prevProps: NodeProps, nextProps: NodeProps): boolean => {
  // Position changes - most frequent cause of re-renders
  if (prevProps.x !== nextProps.x || prevProps.y !== nextProps.y) {
    return false;
  }
  
  // State changes - expansion, selection
  if (
    prevProps.isExpanded !== nextProps.isExpanded ||
    prevProps.isSelected !== nextProps.isSelected ||
    prevProps.isRoot !== nextProps.isRoot ||
    prevProps.showExpandButton !== nextProps.showExpandButton
  ) {
    return false;
  }
  
  // Data identity check - if same object reference, likely unchanged
  if (prevProps.data === nextProps.data) {
    return true;
  }
  
  // Deep data comparison for critical fields only
  const prevData = prevProps.data;
  const nextData = nextProps.data;
  
  if (
    prevData.id !== nextData.id ||
    prevData.firstName !== nextData.firstName ||
    prevData.lastName !== nextData.lastName ||
    prevData.profilePicture !== nextData.profilePicture ||
    prevData.treeDepth !== nextData.treeDepth
  ) {
    return false;
  }
  
  // Children count change (indicates structural change)
  const prevChildrenCount = prevData.children?.length ?? 0;
  const nextChildrenCount = nextData.children?.length ?? 0;
  if (prevChildrenCount !== nextChildrenCount) {
    return false;
  }
  
  // Function reference stability (should be stable with useCallback)
  if (prevProps.onClick !== nextProps.onClick || prevProps.onExpand !== nextProps.onExpand) {
    return false;
  }
  
  return true;
};

const NodeComponent: React.FC<NodeProps> = ({
  data,
  x,
  y,
  onClick,
  onExpand,
  isRoot = false,
  isExpanded = false,
  isSelected = false,
  showExpandButton = true,
}) => {
  const handleClick = () => {
    onClick?.(data);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExpand?.(data);
  };

  const hasChildren = data.children && data.children.length > 0;
  const showMemberCount = data.memberCount !== undefined || data.partnerCount !== undefined;
  const isInactive = data.status !== "Active";
  
  // Create unique IDs for ARIA relationships
  const nodeId = `tree-node-${data.id}`;
  const nodeLabelId = `tree-node-label-${data.id}`;
  const nodeDescId = showMemberCount ? `tree-node-desc-${data.id}` : undefined;
  const expandButtonId = hasChildren && showExpandButton ? `tree-node-expand-${data.id}` : undefined;

  // Root node has simplified styling
  if (isRoot) {
    return (
      <Box
        sx={{
          marginLeft: 1,
          position: 'absolute',
          left: x,
          top: y,
          zIndex: 10,
        }}
      >
        <NodeContainer
          id={nodeId}
          isRoot={true}
          elevation={0}
          onClick={handleClick}
          role="treeitem"
          tabIndex={-1}
          aria-level={1}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-selected={isSelected}
          aria-labelledby={nodeLabelId}
          aria-describedby={nodeDescId}
          sx={{
            borderColor: 'primary.main',
            backgroundColor: 'background.paper',
            border: 'none',
            boxShadow: 'none',
            padding: 1,
            gap: 1,
            width: 180, // Consistent width with other nodes
          }}
        >
          {/* Root Avatar - larger and simpler */}
          <Avatar
            src={data.profilePicture}
            alt={`${data.firstName} ${data.lastName}`}
            aria-label={`${data.firstName} ${data.lastName}`}
            aria-hidden="true"
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.8rem',
            }}
          />
          
          {/* Root Content - just name */}
          <NodeContent>
            <NodeName 
              id={nodeLabelId}
              sx={{ fontSize: '0.85rem', fontWeight: 600 }}
              role="none"
            >
              {data.firstName} {data.lastName}
            </NodeName>
          </NodeContent>
        </NodeContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        left: x,
        top: y,
        zIndex: 10,
      }}
    >
      <NodeContainer
        id={nodeId}
        isRoot={data.treeDepth === 0}
        elevation={0}
        onClick={handleClick}
        role="treeitem"
        tabIndex={-1}
        aria-level={data.treeDepth + 1}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isSelected}
        aria-labelledby={nodeLabelId}
        aria-describedby={nodeDescId}
        aria-disabled={isInactive}
        sx={{
          borderColor: 'divider',
          backgroundColor: isSelected ? 'primary.main' : 'background.paper',
          borderWidth: 1,
          color: isSelected ? 'primary.contrastText' : 'text.primary',
          opacity: isInactive ? 0.2 : 1,
          filter: isInactive ? 'grayscale(1)' : 'none',
          '& .MuiTypography-root': {
            color: isSelected ? 'primary.contrastText' : 'inherit',
          },
          '& .MuiSvgIcon-root': {
            color: isSelected ? 'primary.contrastText' : 'inherit',
          },
        }}
      >
        {/* Level Badge or Leg Position Label - only for non-root nodes */}
        {data.treeDepth > 0 && (
          <>
            {/* Show Leg Position Label only for customers with legLevel 1 or 2 */}
            {data.legLevel === 1 || data.legLevel === 2 ? (
              <Chip
                color={data.legLevel === 1 ? 'unicityGreen' : 'unicityBlue'}
                size="small"
                label={`Leg ${data.legLevel}`}
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                }}   
              />
            ) : (
              /* Show Level Badge for everyone else (legLevel 3 and all deeper levels) */
              <LevelBadge
                sx={{
                  backgroundColor: isSelected ? 'primary.contrastText' : 'primary.main',
                  color: isSelected ? 'primary.main' : 'primary.contrastText',
                }}
              >
                {data.treeDepth}
              </LevelBadge>
            )}
          </>
        )}
        
        {/* Avatar */}
        <Avatar
          src={data.profilePicture}
          alt={`${data.firstName} ${data.lastName}`}
          aria-label={`${data.firstName} ${data.lastName}`}
          aria-hidden="true"
          sx={{
            width: 20,
            height: 20,
          }}
        />
        
        {/* Content */}
        <NodeContent>
          <NodeName 
            id={nodeLabelId}
            role="none"
          >
            {data.firstName} {data.lastName}
          </NodeName>
                    
          {showMemberCount && (
            <MemberCountDisplay 
              id={nodeDescId}
              role="none"
            >
              {data.memberCount || 0} Members{data.partnerCount ? ` | ${data.partnerCount} Partners` : ''}
            </MemberCountDisplay>
          )}
        </NodeContent>
        
        {/* Expand Button - only for non-root nodes */}
        {hasChildren && showExpandButton && (
          <ExpandButton
            id={expandButtonId}
            onClick={handleExpandClick}
            aria-label={isExpanded ? `Collapse ${data.firstName} ${data.lastName}` : `Expand ${data.firstName} ${data.lastName}`}
            aria-controls={nodeId}
            aria-expanded={isExpanded}
            sx={{
              backgroundColor: isSelected ? 'primary.contrastText' : 'background.paper',
              borderColor: isSelected ? 'primary.contrastText' : 'divider',
              color: isSelected ? 'primary.main' : 'inherit',
              // Force perfect circular shape - override Material-UI defaults
              width: '16px !important',
              height: '16px !important',
              minWidth: '16px !important',
              minHeight: '16px !important', // Override the 44px min-height
              maxWidth: '16px !important',
              maxHeight: '16px !important',
              borderRadius: '50% !important',
              padding: '0 !important',
              margin: '0 !important',
              boxSizing: 'border-box !important',
              '&:hover': {
                backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.9)' : 'action.hover',
              },
            }}
          >
            {isExpanded ? (
              <ExpandMore aria-hidden="true" />
            ) : (
              <ChevronRight aria-hidden="true" />
            )}
          </ExpandButton>
        )}
      </NodeContainer>
    </Box>
  );
};

// Export memoized version with custom comparison
export const Node = React.memo(NodeComponent, areNodePropsEqual); 